/**
 * BlockNoteArtifactEditor — thin wrapper around BlockNote's Mantine view
 * used inside {@link StreamedArtifactSidePanel} for the WYSIWYG edit
 * mode. Isolated in its own file so the BlockNote runtime + Mantine CSS
 * only load when the user actually enters edit mode.
 *
 * Contract:
 *   • `initialMarkdown` seeds the editor once on mount. Subsequent typing
 *     lives inside BlockNote's internal doc — parent reads it back via
 *     `onReady(editor)` and calls `editor.blocksToMarkdownLossy(...)`
 *     when Save is clicked.
 *   • `onReady` fires with the live editor so the parent can hold a ref
 *     without React re-rendering on every keystroke.
 */

import React from 'react';
import { BlockNoteEditor } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';

export interface BlockNoteArtifactEditorProps {
  initialMarkdown: string;
  onReady: (editor: BlockNoteEditor) => void;
  isRtl?: boolean;
  isDark?: boolean;
}

export const BlockNoteArtifactEditor: React.FC<BlockNoteArtifactEditorProps> = ({
  initialMarkdown,
  onReady,
  isRtl,
  isDark,
}) => {
  const editor = useCreateBlockNote();

  // Seed the editor from markdown once, right after mount. BlockNote's
  // `tryParseMarkdownToBlocks` is async — we swallow errors and fall back
  // to the raw text as a single paragraph so nothing gets lost.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const blocks = await editor.tryParseMarkdownToBlocks(initialMarkdown ?? '');
        if (cancelled) return;
        if (blocks.length > 0) {
          editor.replaceBlocks(editor.document, blocks);
        }
      } catch (err) {
        console.warn('Failed to parse markdown into BlockNote blocks:', err);
      } finally {
        if (!cancelled) onReady(editor);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return (
    <div
      dir={isRtl ? 'rtl' : undefined}
      className="rounded-md bg-white dark:bg-gray-800 min-h-[300px] max-w-full"
      data-testid="streamed-artifact-editor"
    >
      <BlockNoteView
        editor={editor}
        theme={isDark ? 'dark' : 'light'}
        className="blocknote-editor"
      />
    </div>
  );
};

export default BlockNoteArtifactEditor;
